import React, { useCallback, useContext, useEffect, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { NOTIFICATION_HUB_URL } from 'api/employeeManual';
import PropTypes from 'prop-types';
import AuthContext from 'contexts/auth-context';
import usePositionRequestCount from 'hooks/usePositionRequestCount';
import { getAuthToken, getEmployeeId, isAdmin } from 'utils/auth';

const NotificationContext = React.createContext();
const POSITION_RECONCILIATION_INTERVAL_MS = 60 * 1000;

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    return {
      latestNotification: null,
      connection: null,
      clearLatestNotification: () => {},
      pendingPositionCount: null,
      pendingPositionCountLoading: false,
      pendingPositionCountError: '',
      refreshPendingCount: () => {},
      refreshPositionRequests: () => {},
      positionRequestsRevision: 0,
    };
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { isLoggedIn } = useContext(AuthContext);
  const employeeId = isLoggedIn ? getEmployeeId() : null;
  const countState = usePositionRequestCount(!!employeeId && isAdmin(), employeeId);
  const [positionRequestsRevision, setPositionRequestsRevision] = useState(0);
  const { refreshPendingCount } = countState;
  const refreshPositionRequests = useCallback(() => {
    setPositionRequestsRevision((revision) => revision + 1);
    return refreshPendingCount();
  }, [refreshPendingCount]);
  const [latestNotification, setLatestNotification] = useState(null);
  const [connection, setConnection] = useState(null);

  const clearLatestNotification = () => setLatestNotification(null);

  useEffect(() => {
    setLatestNotification(null);
    setConnection(null);
    setPositionRequestsRevision(0);
    if (!employeeId) {
      return;
    }
    let disposed = false;
    let retryTimer;
    const newConnection = new HubConnectionBuilder()
      .withUrl(NOTIFICATION_HUB_URL, {
        accessTokenFactory: () => getAuthToken() || '',
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    newConnection.on("JobFinished", (status, timestamp) => {
      if (disposed) { return; }
      setLatestNotification({ 
        status, 
        timestamp: new Date(timestamp),
        receivedAt: new Date()
      });
    });

    const reconcilePositionState = () => {
      if (!disposed) {
        refreshPositionRequests();
      }
    };
    newConnection.on('PositionRequestsChanged', reconcilePositionState);
    newConnection.on('MyPositionChanged', reconcilePositionState);
    if (typeof newConnection.onreconnected === 'function') {
      newConnection.onreconnected(reconcilePositionState);
    }
    // SignalR events provide immediate updates. This low-frequency reconciliation
    // recovers from events missed while the browser was disconnected.
    const reconciliationTimer = setInterval(
      reconcilePositionState,
      POSITION_RECONCILIATION_INTERVAL_MS
    );

    const startConnection = async () => {
      try {
        await newConnection.start();
        if (disposed) { await newConnection.stop().catch(() => {}); return; }
        setConnection(newConnection);
      } catch (err) {
        if (!disposed) {
          console.error("SignalR Connection Error: ", err);
          retryTimer = setTimeout(startConnection, 5000);
        }
      }
    };

    startConnection();

    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      clearInterval(reconciliationTimer);
      newConnection.off("JobFinished");
      newConnection.off('PositionRequestsChanged');
      newConnection.off('MyPositionChanged');
      newConnection.stop().catch(() => {});
    };
  }, [employeeId, refreshPositionRequests]);

  return (
    <NotificationContext.Provider value={{
      latestNotification, connection, clearLatestNotification,
      ...countState, positionRequestsRevision, refreshPositionRequests,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

NotificationProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
